import { useCallback, useRef, useState } from "react";
import { ReceiptData } from "./receiptTemplate";
import { printReceipt, PrintReceiptResult } from "./receiptPrinter";

// Shared double-click guard for both of this app's print-receipt call
// sites (CheckoutPage's Print Receipt, OrderPage's Generate Bill).
//
// The actual cross-instance/cross-tab exclusion is enforced inside
// printReceipt() itself (a module-scoped lock, Web Locks API where
// available) — a ref here is scoped to one mounted hook instance, so on
// its own it wouldn't stop a print started on CheckoutPage, still awaiting
// USB device acquisition, from racing a second one started after
// navigating to OrderPage (a fresh hook instance, fresh ref) or from a
// second browser tab. printingRef exists for a *fast, synchronous* guard
// against a double-click on the same button before the async
// printReceipt() call has even had a chance to acquire the real lock —
// belt-and-suspenders, not the primary defense.
//
// `id` distinguishes *which* print is in flight for display purposes (e.g.
// OrderPage disabling every row's button while showing "Printing…" on just
// the clicked one) — the guard itself is global per hook instance, not
// per-id, since the underlying USB connection is one shared resource that
// different ids (different orders' bills) would also collide on.
export function usePrintReceipt() {
  const [printingId, setPrintingId] = useState<string | null>(null);
  // Guards the actual concurrency check — a ref so the check inside
  // `print` always sees the latest value synchronously, not a value from
  // whatever render closed over this callback.
  const printingRef = useRef(false);

  const print = useCallback(
    async (id: string, getData: () => ReceiptData | Promise<ReceiptData>): Promise<PrintReceiptResult | null> => {
      if (printingRef.current) return null;
      printingRef.current = true;
      setPrintingId(id);
      try {
        return await printReceipt(getData);
      } finally {
        printingRef.current = false;
        setPrintingId(null);
      }
    },
    []
  );

  return { printingId, isPrinting: printingId !== null, print };
}
